/**
 * Quick Study — pure, dependency-light helpers extracted from QuickStudyPage so the
 * highest-risk logic (segment cuts, monadic version labels, the next-steps block) is
 * unit-testable on its own (the page module drags in React/deck-ui and isn't
 * import-friendly in tests).
 */
import { CUT_DIRECTIONAL_N, CUT_SUPPRESS_N } from '@digital-twins/deck-ui/lib/aggregation'

import type { Result } from './quickStudyTypes'
import { RespondentNoun, respondentNoun } from './respondentNoun'
import { isCleanSegmentKey, humanizeAttr, displayAttrValue, qualifiedCutDisplayLabel, segPriority, isConsumerDemographicAttr } from './audience-segments'
import { orderSegmentBands } from './segmentBreakdown'

// The single-question answer key inside each twin's survey_data (mirrors backend _Q_NAME).
export const QS_MAIN = 'qs_main'
// QS-42 — a lightweight live tally of answers landed so far, shown in the progress
// bubble so the distribution "fills in" as twins respond.
export type PartialTally =
  | { kind: 'bars'; items: Array<{ label: string; count: number }>; n: number }
  | { kind: 'stat'; label: string; value: number; n: number }
  | { kind: 'count'; n: number }

export interface MigrationMatrixRow {
  scenario: string
  anchor_price: number | null
  share_at_anchor_pct: number
  none_pct: number
  lever_level: string
  price_ladder?: Array<number>
  /** Currency symbol carried from the round's spec so the chart never hardcodes "$". */
  currency?: string
}

/** P1 — should the "Run more twins to confirm" affordance show on this insight?
 *  Keys off the AUTHORITATIVE backend QS-53 flag (too_close_to_call), NOT the one-run
 *  `agreement` spread used by the debate button — a low spread on a clear, well-separated
 *  winner must NOT offer a top-up. Also requires the stored generation spec (so the SAME
 *  question can be re-issued; legacy saved sessions lack it) and headroom under the cap
 *  (no point topping up a run that already sampled the whole audience / hit the max). */
export function canTopUp(opts: {
  tooClose?: boolean
  hasSpec: boolean
  total: number
  cap: number
}): boolean {
  return opts.tooClose === true && opts.hasSpec && opts.total < opts.cap
}

// QS-67 — the twins-per-question control clamped the MINIMUM on every keystroke, so
// you couldn't type "12" (the "1" snapped to 5 → "52"). The pane now keeps a free
// draft string and only clamps on commit/blur via clampTwins. Pure + exported.
// QS credits: 10 is the sample-mode floor — under 10 isn't statistically meaningful,
// so the UI steers the user to a focus group instead (see QuickStudyTwinsPanel).
export const TWINS_MIN = 10
// True-monadic Step 5 — honesty suffix appended to the monadic base-size captions whenever
// the twin-monadic fallback actually reused a twin for a second read. Plain wording ("N twins
// rated more than once") replaces the "unique respondents / reused threads" jargon — it states
// the design fact (some twins saw more than one item) without the researcher vocabulary, and
// generalizes to any number of versions. '' (nothing appended) when `nReused` is absent/0 — an
// all-unique panel's caption is unchanged, so this is purely additive and never fires before
// python ships `n_reused_threads` (undefined on a legacy/absent result). `_nUnique` is kept in
// the signature for call-site compatibility but is no longer surfaced.
export function honestyThreadsSuffix(
  _nUnique?: number | null,
  nReused?: number | null,
  noun: RespondentNoun = 'twins',
): string {
  if (typeof nReused !== 'number' || !Number.isFinite(nReused) || nReused <= 0) return ''
  return ` · ${nReused} ${respondentNoun(nReused, noun)} rated more than once`
}

// --------------------------------------------------------------------------- #
// Combo quant+OE RETROFIT ("get the whys" on an existing quant card) — Step H, Part 4.
// Pure, tested helpers the thin `retrofitFollowUp` action composes. The action itself only draws
// the panel, streams /run, and patches — all the logic (eligibility, per-twin score stamping,
// result merge) lives here so it's unit-testable without the hook's React/transport machinery.
// --------------------------------------------------------------------------- #

// Whether a stored result can be RETROFIT with a follow-up "why". Necessary conditions the FE can
// see WITHOUT a registry lookup: it isn't already a combo (`paired_oe_kind` unset), it's a stamped
// template stage (free-form/generic cards can't pair), it carries per-twin quant scores to stamp,
// and it isn't itself an open-end. The SERVER is authoritative on whether the stage actually pairs
// — a retrofit against a non-pairable stage fails fast and the merge is a no-op — so this is a
// best-effort CTA gate, never the final say. Survey-agnostic: no question/value branch.
export function canRetrofit(result: Result | null | undefined): boolean {
  if (!result) return false
  if (result.paired_oe_kind) return false
  if (!String((result as any).stage_id ?? '').trim()) return false
  const qt = String((result.question as any)?.type ?? '')
  if (qt === 'comment' || qt === 'text') return false
  const responses = Array.isArray(result.responses) ? result.responses : []
  return responses.some(
    (r) => r?.survey_data != null && r.survey_data[QS_MAIN] != null && r.survey_data[QS_MAIN] !== '',
  )
}

// QS-91 — the amber low-variance ("Near-unanimous") warning used to ALWAYS read
// "Check the options (or creatives) are distinct and that the twins can actually see
// them." On a TEXT-only run (no stimulus image) the "creatives / can actually see
// them" half is wrong advice. `resultHasStimulusImage` is the reliable predicate the
// card branches on: the backend attaches every creative (per-option, or a single
// stimulus) onto `question._qs_images` (twins.run_twins_streaming), so a non-empty
// array there means a stimulus image was shown for THIS result. We also accept a
// per-option `image_url` (matrix concept rows) or a `stimulus_image_url` carried on
// the question, defensively. A missing/empty question → text-case (no image). Pure +
// exported so the image-vs-text-vs-no-question branch is unit-tested without React.
export function resultHasStimulusImage(question: unknown): boolean {
  if (!question || typeof question !== 'object') return false
  const q = question as Record<string, unknown>
  const imgs = q._qs_images
  if (Array.isArray(imgs) && imgs.length > 0) return true
  if (typeof q.stimulus_image_url === 'string' && q.stimulus_image_url.trim() !== '') return true
  // A video creative is a visual stimulus too (the twins watch its storyboard).
  if (typeof q.stimulus_video_url === 'string' && q.stimulus_video_url.trim() !== '') return true
  const choices = q.choices
  if (
    Array.isArray(choices) &&
    choices.some((c) => {
      if (!c || typeof c !== 'object') return false
      const opt = c as Record<string, unknown>
      const img = typeof opt.image_url === 'string' && opt.image_url.trim() !== ''
      const vid = typeof opt.video_url === 'string' && opt.video_url.trim() !== ''
      return img || vid
    })
  ) {
    return true
  }
  return false
}

// QS-91 — the body copy for the amber low-variance warning, branched on whether a
// stimulus image was actually shown. Image case keeps the original "options/creatives
// + can they see them" advice (a creative may be off-brief or unreadable). Text case
// can't blame visibility, so it points at the likelier causes for an all-text run.
export function lowVarianceHint(question: unknown): string {
  return resultHasStimulusImage(question)
    ? 'Check the options (or creatives) are distinct and that the respondents can actually see them.'
    : 'One option may simply dominate, or the respondents over-converged. Try a closer pair or a rating scale.'
}

// ★ Stimulus grounding — did THIS result run TEXT-ONLY even though the study has a
// creative attached? The big QS realism bug was follow-up questions silently dropping
// the creative (see pickCreatives), so the twins answered about an ad they never saw.
// pickCreatives now persists it; this stays as the visible safety net: when the study
// had a creative AT RUN TIME yet this result shows no stimulus image, the run was
// ungrounded — surface it instead of letting it pass as grounded. `studyHadCreative` MUST
// be the study's creative state at RUN TIME (not "now"), so a genuine text question asked
// before any creative was attached is never falsely flagged. Pure + exported.
export function ranTextOnly(question: unknown, studyHadCreative: boolean): boolean {
  return studyHadCreative === true && !resultHasStimulusImage(question)
}

/** Defensive scrub of a machine payload the narrative writer sometimes leaks as trailing
 * text: the `next_steps` array of {label,hint,send,kind} objects (it drives the suggestion
 * chips — it must NEVER be shown as prose). Keyed on the UNDERSCORED backend key, so natural
 * prose like "next steps" (with a space) is left untouched. Cuts from the first such marker
 * to the end (including any leading list bullet), leaving only the human narrative. Pure. */
export function stripLeakedNextSteps(text: string): string {
  const s = text ?? ''
  const marker = s.search(/(?:^|\n)\s*(?:[-*+]\s*)?["']?next_steps["']?\s*['"]?\s*[:=]/i)
  if (marker === -1) return s
  return s.slice(0, marker).replace(/\s+$/, '')
}

/** Split critique markdown into the LLM lead paragraph and the bullet body. The backend
 * joins them as `lead + "\\n\\n" + "- bullet\\n- …"`; comparison labels are bolded in UI. */
export function splitCritiqueLead(critique: string): { lead: string; body: string } {
  const text = stripLeakedNextSteps((critique ?? '').trim()).trim()
  if (!text) return { lead: '', body: '' }
  const splitAt = text.search(/\n\n(?=- )/)
  if (splitAt === -1) {
    if (text.startsWith('- ')) return { lead: '', body: text }
    return { lead: text, body: '' }
  }
  return { lead: text.slice(0, splitAt).trim(), body: text.slice(splitAt).trim() }
}

// Per-card storytelling optimization (Phase 2) — subtle low-sample signal for the card
// narrative. Reuses the SAME base-size the card's own footer already reads
// (`coverageFooter`): `n_per_creative` for a monadic card (the true per-item base), else
// the pooled `n`. Floor reuses `TWINS_MIN` (10) — already this codebase's "under this
// isn't statistically meaningful" line (see `clampTwins`) — rather than inventing a
// second, competing threshold. `base === 0` (no data yet / still querying) is NOT
// flagged — the card has nothing to caveat until it has a base to show. Pure.
export function isLowSampleCard(r: { monadic?: boolean; n_per_creative?: number; n: number }): boolean {
  const base = r.monadic && r.n_per_creative ? r.n_per_creative : r.n
  return Number.isFinite(base) && base > 0 && base < TWINS_MIN
}

export type TextSegment = { text: string; bold: boolean }

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function uniqueComparisonLabels(labels: string[]): string[] {
  return [...new Set(labels.map((l) => l.trim()).filter((l) => l.length >= 2))].sort(
    (a, b) => b.length - a.length,
  )
}

/** Labels for entities being compared (monadic items, question choices, etc.). */
export function collectComparisonLabels(result: {
  per_item?: Array<{ label?: string }>
  monadic_themes?: Array<{ variants?: Array<{ label?: string }> }>
  monadic_rating?: Array<{ label?: string }>
  monadic_boolean?: Array<{ label?: string }>
  monadic_numeric?: Array<{ label?: string }>
  monadic_choice?: Array<{ label?: string; choices?: Array<{ choice_label?: string }> }>
  monadic_rank?: Array<{ label?: string; ranked?: Array<{ choice_label?: string }> }>
  option_metadata?: Array<{ label?: string }>
  dissent?: { label?: string } | null
  question?: { choices?: Array<{ text?: string; value?: unknown }> } | null
}): string[] {
  const labels = new Set<string>()
  const add = (raw?: string) => {
    const t = (raw ?? '').trim()
    if (t.length >= 2) labels.add(t)
  }
  for (const item of result.per_item ?? []) add(item.label)
  for (const theme of result.monadic_themes ?? []) {
    for (const variant of theme.variants ?? []) add(variant.label)
  }
  for (const row of result.monadic_rating ?? []) add(row.label)
  for (const row of result.monadic_boolean ?? []) add(row.label)
  for (const row of result.monadic_numeric ?? []) add(row.label)
  for (const row of result.monadic_choice ?? []) {
    add(row.label)
    for (const choice of row.choices ?? []) add(choice.choice_label)
  }
  for (const row of result.monadic_rank ?? []) {
    add(row.label)
    for (const rank of row.ranked ?? []) add(rank.choice_label)
  }
  for (const opt of result.option_metadata ?? []) add(opt.label)
  add(result.dissent?.label)
  for (const choice of result.question?.choices ?? []) {
    add(choice.text)
    if (choice.value != null && String(choice.value).trim()) add(String(choice.value))
  }
  return uniqueComparisonLabels([...labels])
}

/** Split prose into normal vs comparison-label spans (longest label match wins). */
export function splitTextWithBoldLabels(text: string, labels: string[]): TextSegment[] {
  const unique = uniqueComparisonLabels(labels)
  if (!text || unique.length === 0) return [{ text, bold: false }]
  const re = new RegExp(`(${unique.map(escapeRegExp).join('|')})`, 'gi')
  const segments: TextSegment[] = []
  let last = 0
  for (const match of text.matchAll(re)) {
    const idx = match.index ?? 0
    if (idx > last) segments.push({ text: text.slice(last, idx), bold: false })
    segments.push({ text: match[0], bold: true })
    last = idx + match[0].length
  }
  if (last < text.length) segments.push({ text: text.slice(last), bold: false })
  return segments.length ? segments : [{ text, bold: false }]
}

/** Return the text unchanged — comparison labels (creatives, landing pages) are NOT
 *  emphasized in bold. Kept as a pass-through so call sites keep a single, stable seam
 *  for any future label styling. */
export function emphasizeComparisonLabels(text: string, _labels: string[]): string {
  return text
}

/** Minimum answered questions before we recommend the focus group as the wrap-up. */
export const DEFAULT_ENOUGH = 3

/** Per-template targets. All ≤ 4 (user's stated max); psm/pain are self-contained at 2. */
export const ENOUGH_QUESTIONS: Record<string, number> = {
  lp_conversion: 3,
  ad_creative: 3,
  concept_validation: 3,
  claim_validation: 3,
  brand_perception: 3,
  pain_ranking: 2,
  pricing_packaging: 2,
}

/** How many questions makes a study "enough" for this goal.
 *  PSM (Van Westendorp) is a content template, not a question type — it is detected via
 *  its result marker (plot==='psm'), passed in as `isPsm`, regardless of goal. */
export function enoughFor(goalId?: string | null, isPsm?: boolean): number {
  if (isPsm) return ENOUGH_QUESTIONS['pricing_packaging'] ?? DEFAULT_ENOUGH
  return ENOUGH_QUESTIONS[goalId ?? ''] ?? DEFAULT_ENOUGH
}

// --------------------------------------------------------------------------- //
// QS — consolidated "Suggested next steps". The insight card folds its scattered
// affordances (study chips + focus group + debate + run-more-twins) into ONE lettered
// list (a/b/c) with a short wrap-up intro and exactly ONE Recommended move. The
// recommendation is ARC-AWARE: it keys off whether an `advance` question was already
// answered (`advanceConsumed`) or the count backstop (`answered >= enough`), not just
// the single result in front of it. These helpers are PURE so the intro copy, ordering,
// capping, and recommendation are all unit-testable without React.
// --------------------------------------------------------------------------- //

/** A short, deterministic wrap-up line shown above the next steps. Keyed only on
 *  flags the card already has — so it never contradicts the recommendation and is
 *  never empty. (Deliberately deterministic, not LLM: the line must not name a
 *  specific option, so an LLM variant would carry almost no extra signal.)
 *  When the study hasn't yet reached arc maturity (`answered < enough` and no
 *  advance question consumed), leads forward instead of closing out. */
export function nextStepsIntro(
  result: { too_close_to_call?: boolean; low_variance?: boolean } | null | undefined,
  opts?: { answered?: number; enough?: number; advanceConsumed?: boolean; advanceAvailable?: boolean },
): string {
  if (result?.too_close_to_call) return "It's close, here's how I'd settle it."
  // Maturity mirrors pickRecommendedKey: while the arc still has a forward move
  // (advanceAvailable) NOTHING closes out — neither a consumed advance nor the count backstop —
  // so the intro keeps leading forward to the next question right up to the terminal stage. Only
  // once no advance lane remains does a consumed advance OR the count backstop mature the arc.
  // When opts are absent (legacy callers / standalone tests), treat as mature.
  const mature = opts == null
    || (opts.advanceAvailable !== true
        && (opts.advanceConsumed === true || (opts.answered ?? 0) >= (opts.enough ?? DEFAULT_ENOUGH)))
  if (!mature) return "Good start, here's the next thing I'd test."
  if (result?.low_variance) return "Near-unanimous, here's where I'd dig in next."
  return "Clear read, here's where I'd take it next."
}

/** A short, grounded topic clause for a focus group launched from THIS result — used in
 *  the next-step hint ("Invite N twins to discuss <topic>") and the room opener. Keyed
 *  only on fields the card already has; never invents a winner label or a number, so it
 *  stays grounded. Pure + exported so the copy is unit-tested without React. */
export function focusGroupTopic(
  result: {
    question?: { type?: string } | null
    spread_note?: string
    too_close_to_call?: boolean
    agreement?: number
  } | null | undefined,
): string {
  if (!result) return 'this result'
  const qt = String(result.question?.type ?? '')
  const note = (result.spread_note ?? '').toLowerCase()
  // A genuine split / near-tie → the discussion is about WHY it's close, not the winner.
  const close =
    result.too_close_to_call === true ||
    note.includes('split') ||
    note.includes('mixed') ||
    (typeof result.agreement === 'number' && result.agreement <= 0.6)
  if (['radiogroup', 'dropdown', 'ranking'].includes(qt)) {
    return close ? "why it's so close" : 'why the winner came out on top'
  }
  if (qt === 'rating' || qt === 'numeric') return "what's behind the score"
  if (qt === 'boolean') return 'why they answered that way'
  if (qt === 'comment') return 'the top theme'
  return 'this result'
}

export type NextStepKind = 'study' | 'focus_group' | 'debate' | 'more_twins'

export interface NextStepItem {
  /** Stable key — the React key, the selection id stored on the insight message, and
   *  the click target. Study items use `study-<index>`; actions use their kind. */
  key: string
  kind: NextStepKind
  /** Backend `Suggestion.kind` for study items (deepen|advance|angle); "" for actions. */
  laneKind: string
  label: string
  hint: string
  /** Study items only — the marketer-phrased message dispatched on click. */
  send: string
  recommended: boolean
}

export interface BuildNextStepsInput {
  /** Study chips from the result (already segment-deduped + ask_next-backfilled by the card). */
  suggestions: Array<{ label: string; send: string; hint?: string; kind?: string }>
  showFocusGroup: boolean
  showDebate: boolean
  showMoreTwins: boolean
  /** Arc-maturity drivers — the focus group is recommended once the study is "rich enough". */
  /** 1-based ordinal of this insight card among all insight cards in the study. */
  answered: number
  /** Minimum answered questions to recommend the focus group (from enoughFor). */
  enough: number
  /** True when any PRIOR result carried arc_kind === 'advance' (arc decision answered). */
  advanceConsumed: boolean
  tooClose: boolean
  /** Hint for the more-twins action (the result's margin_note); falls back when "". */
  marginNote?: string
  /** Topic-specific hint for the focus-group action (built by the card from
   *  `focusGroupTopic` + the seated twin count); falls back to the generic line when "". */
  focusGroupHint?: string
}

export const NEXT_STEPS_MAX = 3

/** Temporary kill-switch for the insight-card A–C next-steps bubble (study chips +
 *  focus group / debate / more twins). Flip to `true` to restore the block — builder,
 *  recommendation, and backend suggestions stay intact. */
export const SHOW_QS_NEXT_STEPS = false

const FOCUS_GROUP_HINT = 'Your twins discuss this insight live'
const DEBATE_HINT = 'Two twins debate the near-tie'
const MORE_TWINS_HINT = 'Re-run larger to settle the lead'

/** Build the consolidated, ordered a–c next-steps list with exactly one Recommended
 *  move. Study chips lead (the "what to ask next"); the focus group / more-twins /
 *  debate actions follow. Capped at three, PINNING the recommended move and the focus
 *  group so neither is trimmed when a split / too-close result adds extra actions. */
export function buildNextSteps(input: BuildNextStepsInput): NextStepItem[] {
  const study: NextStepItem[] = input.suggestions.map((s, i) => ({
    key: `study-${i}`,
    kind: 'study',
    laneKind: (s.kind || '').trim(),
    label: s.label,
    hint: s.hint || '',
    send: s.send,
    recommended: false,
  }))
  const actions: NextStepItem[] = []
  if (input.showFocusGroup) {
    actions.push({ key: 'focus_group', kind: 'focus_group', laneKind: '', label: 'Start a focus group', hint: (input.focusGroupHint || '').trim() || FOCUS_GROUP_HINT, send: '', recommended: false })
  }
  if (input.showMoreTwins) {
    actions.push({ key: 'more_twins', kind: 'more_twins', laneKind: '', label: 'Run more twins to confirm', hint: (input.marginNote || '').trim() || MORE_TWINS_HINT, send: '', recommended: false })
  }
  if (input.showDebate) {
    actions.push({ key: 'debate', kind: 'debate', laneKind: '', label: 'Discuss the split', hint: DEBATE_HINT, send: '', recommended: false })
  }

  const ordered = [...study, ...actions]
  // Decide the single Recommended move over the FULL list, then cap while pinning it.
  const recommendedKey = pickRecommendedKey(ordered, input)
  const pinned = [recommendedKey, input.showFocusGroup ? 'focus_group' : '']
  const capped = capWithPins(ordered, NEXT_STEPS_MAX, pinned)
  return capped.map((it) => ({ ...it, recommended: it.key === recommendedKey }))
}

/** Recommendation priority:
 *  1. too-close lead → settle it (more twins).
 *  2. arc mature → focus group. Maturity means the study has made its key quantitative
 *     decision: an advance question was consumed, OR there is no advance move left AND the
 *     question-count backstop is hit. A raw count must NEVER pre-empt an AVAILABLE advance —
 *     while the arc still has a forward move, we recommend it (respect the arc).
 *  3. else → the research-arc flow move (`advance`); else first study chip; else first item.
 *  The focus group is the qualitative wrap-up: recommended only once the study has made
 *  its key quantitative decision(s). Before that, keep pushing the arc forward. */
function pickRecommendedKey(items: NextStepItem[], input: BuildNextStepsInput): string {
  const has = (k: string): boolean => items.some((i) => i.key === k)
  if (input.tooClose && has('more_twins')) return 'more_twins'
  // Completing the research arc takes precedence: while a forward stage remains (an `advance`
  // lane chip is present), ALWAYS recommend it — regardless of how many questions have run
  // (`answered >= enough`) or whether an advance was already consumed (`advanceConsumed`). For
  // a structured arc (e.g. ad testing) the next question must be the recommendation right up to
  // the terminal intent stage; only once no advance lane is left does the focus-group wrap-up
  // become the recommended move.
  const advance = items.find((i) => i.kind === 'study' && i.laneKind === 'advance')
  if (advance) return advance.key
  const mature = input.advanceConsumed || input.answered >= input.enough
  if (mature && has('focus_group')) return 'focus_group'
  const firstStudy = items.find((i) => i.kind === 'study')
  if (firstStudy) return firstStudy.key
  return items[0]?.key ?? ''
}

/** Cap `ordered` to `max`, guaranteeing the `pinned` keys survive (in their original
 *  positions) and backfilling the rest in order. Pure + order-preserving. */
function capWithPins(ordered: NextStepItem[], max: number, pinned: Array<string>): NextStepItem[] {
  if (ordered.length <= max) return ordered
  const keep = new Set<string>()
  for (const key of pinned) {
    if (key && keep.size < max && ordered.some((i) => i.key === key)) keep.add(key)
  }
  for (const it of ordered) {
    if (keep.size >= max) break
    keep.add(it.key)
  }
  return ordered.filter((i) => keep.has(i.key))
}

// Minimal shape we read off a twin: only its population attributes matter here.
type SegmentTwin = { attributes?: Record<string, unknown> | null }

export type SegmentQuestion = {
  name: string
  title: string
  type: 'radiogroup'
  isPopulationAttribute: true
  choices: { value: string; text: string }[]
}

// QS-8 — per-value twin counts for the active segment cut; flag the thin ones so a
// ~10-twin cell isn't read with false confidence. Only counts twins that actually
// answered the question (a missing answer wouldn't appear in the segmented chart).
export const MIN_SEGMENT_N = 10
type SliceableTwin = { attributes?: Record<string, unknown> | null }

// ── Cut-ID slices (the Compare-segments picker seam) ─────────────────────────
// The picker selects individual CUT IDs — possibly spanning attributes (e.g. "female" + an
// authored segment) rather than taking ONE attribute and splitting it across ALL its values.
// This builds a response subset PER selected cut id, so the generalized band
// renderers can re-derive their own stats off each slice. Two cut-id kinds, matched the
// way each was minted (buildDemographicCutGroups vs buildAuthoredSegmentGroups):
//   - "attribute::value" — demographic cut, matched against `attributes[attribute]`.
//   - bare label         — authored subsegment, matched against `segment_labels[]`.
// The "::" split is kept LOCAL (not imported from divergingStacked.ts's cutId/parseCutId):
// quickStudyPure is upstream of divergingStacked — which imports buildSegmentCandidates from
// here — so importing back would form a module cycle.
const CUT_ID_SEP = '::'

// R1.1 (crafted Wave 0, honesty half) — the floor verdict rides ON the slice, computed ONCE at
// this seam, so every consumer (PlotRenderer's band renderers, InsightCard's quote union, the
// report ledger) inherits the same answer instead of re-deriving its own threshold check (or,
// worse, rendering an unguarded n=1 read). Mirrors DivergingStackedRating's own two-floor
// discipline (`CUT_SUPPRESS_N` / `CUT_DIRECTIONAL_N`) — the SAME Compare-segments-picker
// floors, not a third pair. SC-f2: imported from their home, deck-ui's `lib/aggregation`
// (divergingStacked.ts re-exports the same constants; importing from deck-ui, not from
// divergingStacked, keeps this module out of a cycle). Python's `quick_study/cut_floors.py`
// holds the same values; `test_qs_scorecard_plot.py` checks the pair.

export interface CutIdSlice<T> {
  cutId: string
  /** Display label for the band header ("Male", "Power users", …). */
  label: string
  /** Twins in this cut (raw responses; monadic pools per-item rows before slicing). */
  responses: T[]
  n: number
  /** n < the suppress floor (5) — never chart this cut; name it in the warning line instead. */
  suppressed: boolean
  /** 0 < n < the directional floor (15) — chartable cuts in this range are marked "directional
   *  only". Independent of `suppressed` (mirrors divergingStacked.deriveCutRow); consumers
   *  check suppressed first and only read this flag on bands that survive. */
  directional: boolean
}

type CutSliceableTwin = SliceableTwin & { segment_labels?: string[] | null }

/** Does a twin belong to this cut id? "attribute::value" tests `attributes[attribute]`;
 *  a bare label tests the authored `segment_labels[]`. The single source of truth for cut
 *  membership, shared by every cut-id slicer. */
export function responseInCut(resp: CutSliceableTwin | null | undefined, cutId: string): boolean {
  const sep = cutId.indexOf(CUT_ID_SEP)
  if (sep >= 0) {
    const raw = resp?.attributes?.[cutId.slice(0, sep)]
    return raw != null && typeof raw !== 'object' && String(raw).trim() === cutId.slice(sep + CUT_ID_SEP.length)
  }
  return Array.isArray(resp?.segment_labels) && resp!.segment_labels!.includes(cutId)
}

/** A cut id's fallback label when the caller has no picker label: a demographic cut shows
 *  its translated value (with the humanized attribute prefixed when the bare value alone
 *  is ambiguous — "Tech comfort: 1"), an authored cut its bare label. Hides the `::` codec. */
export function cutIdDisplayLabel(cutId: string): string {
  const sep = cutId.indexOf(CUT_ID_SEP)
  if (sep < 0) return cutId
  return qualifiedCutDisplayLabel(humanizeAttr(cutId.slice(0, sep)), displayAttrValue(cutId.slice(sep + CUT_ID_SEP.length)))
}

/**
 * Build a response slice for each selected cut id, IN SELECTION ORDER (the caller orders
 * bands / prepends the "Everyone" anchor). `labelFor` maps a cut id back to its picker
 * label; absent that, a demographic cut falls back to its translated value and an authored
 * cut to its bare label. Pure — never mutates the input.
 */
export function sliceResponsesByCutIds<T extends CutSliceableTwin>(
  responses: T[],
  cutIds: ReadonlyArray<string>,
  labelFor?: (cutId: string) => string | undefined,
): Array<CutIdSlice<T>> {
  return cutIds.map((cid) => {
    const rows = responses.filter((r) => responseInCut(r, cid))
    const n = rows.length
    // Independent flags, mirroring divergingStacked.deriveCutRow: a suppressed cut can
    // also be directional by the raw arithmetic. Consumers check `suppressed` first and
    // drop the band; `directional` only matters for bands that survive that filter.
    return {
      cutId: cid,
      label: labelFor?.(cid) ?? cutIdDisplayLabel(cid),
      responses: rows,
      n,
      suppressed: n < CUT_SUPPRESS_N,
      directional: n > 0 && n < CUT_DIRECTIONAL_N,
    }
  })
}

/** The cut ids offered for ONE attribute (attribute::value), in the group's option order —
 *  the reroute target when a "break down by <attribute>" chip or `segment_by` default lands.
 *  Reads the picker groups so the ids match exactly what a live pick would produce. */
export function cutIdsForAttribute(
  groups: Array<{ options: Array<{ id: string }> }>,
  attribute: string,
): Array<string> {
  const prefix = `${attribute}${CUT_ID_SEP}`
  const ids: Array<string> = []
  for (const g of groups) {
    for (const o of g.options) {
      if (o.id.startsWith(prefix)) ids.push(o.id)
    }
  }
  return ids
}

// One item's per-segment score bar (mirrors PlotRenderer's MatrixBarDatum shape).
export interface MonadicSegmentItem {
  label: string
  value: number
  n: number
}

export interface MonadicSegmentGroup {
  value: string
  title: string
  items: Array<MonadicSegmentItem>
}

type MonadicPerItem = {
  creative_id: string
  label: string
  responses: Array<SliceableTwin & { survey_data?: Record<string, unknown> | null }>
}

type MonadicRatingMeta = { id: string; label: string; scale_max?: number }

/** Read a twin's scalar rating: prefer `qName`, else the first numeric answer value. */
function scalarAnswer(
  survey_data: Record<string, unknown> | null | undefined,
  qName: string,
): number | null {
  if (!survey_data) return null
  const direct = survey_data[qName]
  if (typeof direct === 'number' && Number.isFinite(direct)) return direct
  for (const v of Object.values(survey_data)) {
    if (typeof v === 'number' && Number.isFinite(v)) return v
  }
  return null
}

/**
 * One per-item ranked bar per SELECTED cut (in selection order), scored on that cut's twins
 * only. Per-item mean / top-2-box math over cut membership (`responseInCut`) rather than over
 * every value of one attribute. Drops a cut with no scored twins so the caller can skip it.
 */
export function monadicRatingSegmentsByCuts(
  perItem: Array<MonadicPerItem>,
  monadicRating: Array<MonadicRatingMeta>,
  cutIds: ReadonlyArray<string>,
  labelFor: (cutId: string) => string,
  qName: string,
  metric: 'mean' | 'top_2_box',
): Array<MonadicSegmentGroup> {
  const scaleFor = new Map<string, number>()
  for (const m of monadicRating) {
    scaleFor.set(m.id, typeof m.scale_max === 'number' ? m.scale_max : 5)
  }
  const groups: Array<MonadicSegmentGroup> = []
  for (const cid of cutIds) {
    let anyScored = false
    const items: Array<MonadicSegmentItem> = perItem.map((it) => {
      const scaleMax = scaleFor.get(it.creative_id) ?? 5
      const nums: number[] = []
      for (const resp of it.responses) {
        if (!responseInCut(resp, cid)) continue
        const v = scalarAnswer(resp.survey_data, qName)
        if (v != null) nums.push(v)
      }
      if (nums.length) anyScored = true
      let value = 0
      if (nums.length) {
        if (metric === 'top_2_box') {
          value = Math.round((100 * nums.filter((v) => v >= scaleMax - 1).length) / nums.length)
        } else {
          value = Number((nums.reduce((a, b) => a + b, 0) / nums.length).toFixed(2))
        }
      }
      return { label: it.label, value, n: nums.length }
    })
    if (!anyScored) continue
    items.sort((a, b) => b.value - a.value)
    groups.push({ value: cid, title: labelFor(cid), items })
  }
  return groups
}

// Offer tidy demographic-style attributes as segment cuts; skip survey-question
// keys (numbered / contain '?') and internal '_'-prefixed fields. Each candidate is a
// radiogroup whose choices are the distinct attribute values (the K buckets), with the
// raw value kept for matching and a translated label shown in the segmented legend.
//
// F1a — `audienceType` ('b2b' | 'b2c' | undefined) suppresses consumer-demographic
// AUTO-cuts (age/gender/income…) on a B2B audience, where they're meaningless: firmographic
// cuts (company size, seniority, industry) still survive. These are AUTO-cuts only — a
// user's chosen saved-tag subsegments are a SEPARATE path and are never gated here, on
// either audience type. Undefined audienceType ⇒ unchanged (every clean key offered).
export function buildSegmentCandidates(responses: SegmentTwin[], audienceType?: string): SegmentQuestion[] {
  if (!responses.length) return []
  const suppressConsumerDemos = String(audienceType ?? '').toLowerCase() === 'b2b'
  const tally = new Map<string, Map<string, number>>()
  for (const resp of responses) {
    for (const [key, raw] of Object.entries(resp?.attributes ?? {})) {
      if (!isCleanSegmentKey(key)) continue
      if (suppressConsumerDemos && isConsumerDemographicAttr(key)) continue
      if (raw == null || typeof raw === 'object') continue
      const val = String(raw).trim()
      if (!val) continue
      if (!tally.has(key)) tally.set(key, new Map())
      const counts = tally.get(key)!
      counts.set(val, (counts.get(val) ?? 0) + 1)
    }
  }
  const out: SegmentQuestion[] = []
  for (const [name, counts] of tally) {
    const distinct = [...counts.keys()]
    if (distinct.length < 2 || distinct.length > 6) continue
    if (distinct.length > Math.max(2, responses.length * 0.6)) continue
    out.push({
      name,
      title: humanizeAttr(name),
      type: 'radiogroup',
      isPopulationAttribute: true,
      // QS-25: keep the raw value for matching twin attributes, but show a translated
      // label (e.g. French "Homme" → "Male") in the segmented chart legend.
      // #17 (J15): ordinal-aware order — age ranges by lower bound, counts numerically,
      // alphabetical fallback — so band choices never render 25-34 before 18-24.
      choices: orderSegmentBands(distinct).map((v) => ({ value: v, text: displayAttrValue(v) })),
    })
  }
  return out.sort((a, b) => segPriority(a.name) - segPriority(b.name)).slice(0, 6)
}

// QS LP QA #23 — the run's authored subsegments (`QuickStudyResult.segment_options`: the
// marketer's chosen saved-tag cuts, label + unique-twin count, in authored order) as the
// "Compare segments" picker's PREPENDED "Audience segments" group, ahead of the derived
// demographic groups built by `buildDemographicCutGroups` (divergingStacked.ts) — same
// `{ groupLabel, options: [{ id, label, n }] }` shape the picker already accepts (its
// props are contract-first / groups-driven for exactly this seam), so a caller can splice
// this group in front with no API change: `[...buildAuthoredSegmentGroups(r.segment_options),
// ...buildDemographicCutGroups(...)]`.
//
// Option `id` is the bare label (never containing the demographic cut id's "attribute::value"
// separator), so a downstream row-derivation step can tell the two cut kinds apart and match
// an authored cut against a response's `segment_labels` array (rather than `attributes`).
// Pure + defensive: a malformed/labelless/zero-n entry is skipped, never invented.
export function buildAuthoredSegmentGroups(
  segmentOptions?: Array<{ label?: string; n?: number }> | null,
  opts?: { includeEmpty?: boolean },
): Array<{ groupLabel: string; options: Array<{ id: string; label: string; n?: number; disabled?: boolean }> }> {
  const includeEmpty = opts?.includeEmpty === true
  const options = (segmentOptions ?? [])
    .filter((o): o is { label: string; n?: number } => typeof o?.label === 'string' && o.label.trim().length > 0)
    .map((o) => {
      const n = typeof o.n === 'number' ? o.n : undefined
      const empty = (n ?? 0) === 0
      return {
        id: o.label.trim(),
        label: o.label.trim(),
        n,
        ...(empty ? { disabled: true } : {}),
      }
    })
    .filter((o) => includeEmpty || (o.n ?? 0) > 0)
  if (options.length === 0) return []
  return [{ groupLabel: 'Audience segments', options }]
}

// R4.4 / crafted Step 5 — SegmentRef → cut id. Attribute refs mint `attr::value`
// (the demographic cut id `responseInCut` already understands). Tag refs mint the
// trimmed NAME, matching `buildAuthoredSegmentGroups` option ids. A `::` inside a
// tag name is refused (catalog already omits those; this is the defensive twin).
// Rename / unknown tag → omitted (silent no-cut fallback). n=0 authored tags stay
// in the result as `emptyBase` so the card can render a disabled chip + note.

export type ExhibitCutRef =
  | { kind: 'attribute'; attribute: string; value: string }
  | { kind: 'tag'; name: string; tagId?: string }
  | string

export interface LoweredExhibitCut {
  cutId: string
  /** Authored tag present on the run with n===0 — chip is shown disabled, not sliced. */
  emptyBase: boolean
}

/** Shape-only: SegmentRef or name-keyed string → cut id, or null if invalid. */
export function nameKeyFromExhibitCut(raw: ExhibitCutRef | null | undefined): string | null {
  if (raw == null) return null
  if (typeof raw === 'string') {
    const s = raw.trim()
    if (!s) return null
    if (s.includes(CUT_ID_SEP)) {
      const attr = s.slice(0, s.indexOf(CUT_ID_SEP)).trim()
      const value = s.slice(s.indexOf(CUT_ID_SEP) + CUT_ID_SEP.length).trim()
      if (!attr || !value || attr.includes(CUT_ID_SEP) || value.includes(CUT_ID_SEP)) {
        return null
      }
      return `${attr}${CUT_ID_SEP}${value}`
    }
    return s
  }
  if (typeof raw !== 'object' || Array.isArray(raw)) return null
  const kind = String((raw as { kind?: unknown }).kind ?? '').trim()
  if (kind === 'attribute') {
    const attr = String((raw as { attribute?: unknown }).attribute ?? '').trim()
    const value = String((raw as { value?: unknown }).value ?? '').trim()
    if (!attr || !value || attr.includes(CUT_ID_SEP)) return null
    return `${attr}${CUT_ID_SEP}${value}`
  }
  if (kind === 'tag') {
    const name = String((raw as { name?: unknown }).name ?? '').trim()
    if (!name || name.includes(CUT_ID_SEP)) return null
    return name
  }
  return null
}

export function nameKeysFromExhibitCuts(refs: ReadonlyArray<ExhibitCutRef>): Array<string> {
  const out: Array<string> = []
  const seen = new Set<string>()
  for (const ref of refs) {
    const key = nameKeyFromExhibitCut(ref)
    if (!key || seen.has(key)) continue
    seen.add(key)
    out.push(key)
  }
  return out
}

function authoredOptionN(
  segmentOptions: Array<{ label?: string; n?: number }> | null | undefined,
  name: string,
): number | undefined {
  const needle = name.trim()
  if (!needle) return undefined
  for (const opt of segmentOptions ?? []) {
    if (typeof opt?.label === 'string' && opt.label.trim() === needle) {
      return typeof opt.n === 'number' ? opt.n : 0
    }
  }
  return undefined
}

function tagKnownOnRun(
  cutId: string,
  segmentOptions: Array<{ label?: string; n?: number }> | null | undefined,
  responses: Array<{ segment_labels?: string[] | null }> | null | undefined,
): boolean {
  if (authoredOptionN(segmentOptions, cutId) !== undefined) return true
  for (const row of responses ?? []) {
    if (Array.isArray(row?.segment_labels) && row.segment_labels.includes(cutId)) {
      return true
    }
  }
  return false
}

function attributeKnownOnRun(
  cutId: string,
  responses: Array<{ attributes?: Record<string, unknown> }> | null | undefined,
): boolean {
  const sep = cutId.indexOf(CUT_ID_SEP)
  if (sep < 0) return false
  const attr = cutId.slice(0, sep)
  if (!responses || responses.length === 0) return true
  return responses.some((row) => {
    const bag = row?.attributes
    return bag != null && Object.prototype.hasOwnProperty.call(bag, attr)
  })
}

/**
 * Lower SegmentRefs (or already-name-keyed strings) to cut ids, then drop
 * renamed/unknown tags. n=0 authored tags stay as `emptyBase`.
 *
 * Unit-tested against `responseInCut` / `segment_labels` fixtures.
 */
export function lowerSegmentRefs(
  refs: ReadonlyArray<ExhibitCutRef>,
  opts?: {
    segmentOptions?: Array<{ label?: string; n?: number }> | null
    responses?: Array<{
      attributes?: Record<string, unknown>
      segment_labels?: string[] | null
    }> | null
  },
): Array<LoweredExhibitCut> {
  const segmentOptions = opts?.segmentOptions
  const responses = opts?.responses
  const out: Array<LoweredExhibitCut> = []
  const seen = new Set<string>()
  for (const ref of refs) {
    const cutId = nameKeyFromExhibitCut(ref)
    if (!cutId || seen.has(cutId)) continue
    const isAttribute = cutId.includes(CUT_ID_SEP)
    if (isAttribute) {
      if (!attributeKnownOnRun(cutId, responses)) continue
      const n = responses?.filter((row) => responseInCut(row, cutId)).length ?? -1
      const emptyBase = n === 0
      seen.add(cutId)
      out.push({ cutId, emptyBase })
      continue
    }
    // Tag / authored name. Rename (not on this run) → silent drop.
    if (responses !== undefined || segmentOptions !== undefined) {
      if (!tagKnownOnRun(cutId, segmentOptions, responses)) continue
    }
    const authoredN = authoredOptionN(segmentOptions, cutId)
    const slicedN =
      authoredN === undefined && responses
        ? responses.filter((row) => responseInCut(row, cutId)).length
        : authoredN
    seen.add(cutId)
    out.push({ cutId, emptyBase: slicedN === 0 })
  }
  return out
}

// ── Spec edit helpers ─────────────────────────────────────────────────────────
// Pure, dependency-free helpers used by ConfirmRunCard to mutate a GenerationSpec
// before the marketer clicks "Ask N twins". All functions are immutable (they return
// a new object, never mutate the input). This keeps mutation testable without React.
//
// Cache scope: one-shot, not cached (called at most once per user interaction).

// --------------------------------------------------------------------------- //
// W0.4 — stat-eyebrow dedupe. The deterministic metric headline renders as a small
// eyebrow under the narrative title ONLY when it adds information. The QS-53 honesty
// caveat is appended to `headline` server-side (load-bearing: the agent's no-winner
// regex parses it), so `headline !== narrative_title` is TRUE even when the headline
// is just the title sentence with the caveat bolted on — which printed the same
// sentence twice on the card. Restatement = normalized headline equals or starts
// with the normalized title. Pure.
// --------------------------------------------------------------------------- //
function normalizeHeadlineForCompare(s: string): string {
  return (s || '')
    .replace(/[*_`#]/g, '')
    .replace(/["“”‘’']/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/[.!?…\s]+$/, '')
}

export function isHeadlineRestatement(narrativeTitle: string | undefined, headline: string | undefined): boolean {
  const title = normalizeHeadlineForCompare(narrativeTitle || '')
  const head = normalizeHeadlineForCompare(headline || '')
  if (!title || !head) return false
  return head === title || head.startsWith(title)
}

// W0.7 follow-up — ONE shared derivation for "which version did this twin see?" on monadic
// pooled quotes, so every quotes surface (rating combo, scorecard combo, any future monadic
// pair) resolves the SAME id→label map and the chip can never be silently absent for one
// result shape. Sources, in precedence order: `per_item` (authored creative labels), the flat
// per-shape monadic series, and the SCORECARD aggregate (its per-version identity is nested
// under each dimension's items — the gap that shipped the claim scorecard without chips).
// Returns undefined when nothing resolves, so callers keep a byte-identical no-chip render.
export interface MonadicVersionLabelSource {
  per_item?: Array<{ creative_id?: string; label?: string }>
  monadic_rating?: Array<{ id?: string; label?: string }>
  monadic_boolean?: Array<{ id?: string; label?: string }>
  monadic_numeric?: Array<{ id?: string; label?: string }>
  monadic_choice?: Array<{ id?: string; label?: string }>
  monadic_rank?: Array<{ id?: string; label?: string }>
  monadic_scorecard?: Array<{ items?: Array<{ id?: string; label?: string }> }>
  // Last resort, and the ONLY source an emotion-profile matrixdropdown has: the server-built
  // matrix rows (`_matrix_row`, twins.py:637 → `{ value: GenOption.id, text: label }`), whose
  // `value` is the same id `_stamp_item_ids` writes onto each response as `item_id`. A radar
  // publishes no per-item list at all (monadic_run.py:1460) and declares no scorecard
  // dimensions, so without this channel every source above is empty and the chip vanishes.
  // Index signature so a whole real question object (type / name / columns / …) is assignable
  // as a literal, not just the one key read here.
  question?: {
    rows?: Array<{ value?: string; id?: string; text?: string }>
    choices?: Array<string | { value?: string; id?: string; text?: string; label?: string }>
    [k: string]: unknown
  }
  // Comparative ranking / choice-of-versions: `analyze` copies spec.options onto every result
  // (`_option_metadata`). Ranking-why feedback is often NOT `r.monadic`, so without this channel
  // the quote chip has ids and no labels (or labels and no map at all).
  option_metadata?: Array<{ id?: string; label?: string; is_none?: boolean }>
}

/** Strip the quote wrapper the backend label minter wraps stimulus labels in ('"A — Depth"' →
 *  'A — Depth'). DISPLAY boundary only — the wire keeps the minted form. One source of truth:
 *  chips, scorecard headers, diverging bars, exports and safeBarLabel all funnel through this,
 *  so the same label can never render three different ways on one screen again. */
export function stripLabelQuotes(label: string | null | undefined): string {
  const l = (label ?? '').trim()
  if ((/^".*"$/.test(l) || /^\u201c.*\u201d$/.test(l)) && l.length >= 2) return l.slice(1, -1).trim()
  return l
}

export function buildMonadicVersionLabelMap(r: MonadicVersionLabelSource): Record<string, string> | undefined {
  const map: Record<string, string> = {}
  for (const it of r.per_item ?? []) {
    if (it.creative_id && (it.label || '').trim()) map[it.creative_id] = stripLabelQuotes(it.label)
  }
  const series = [r.monadic_rating, r.monadic_boolean, r.monadic_numeric, r.monadic_choice, r.monadic_rank]
  for (const list of series) {
    for (const it of list ?? []) {
      if (it.id && !(it.id in map) && (it.label || '').trim()) map[it.id] = stripLabelQuotes(it.label)
    }
  }
  for (const dim of r.monadic_scorecard ?? []) {
    for (const it of dim.items ?? []) {
      if (it.id && !(it.id in map) && (it.label || '').trim()) map[it.id] = stripLabelQuotes(it.label)
    }
  }
  for (const row of r.question?.rows ?? []) {
    const id = row.value ?? row.id
    if (id && !(id in map) && (row.text || '').trim()) map[id] = stripLabelQuotes(row.text)
  }
  for (const choice of r.question?.choices ?? []) {
    if (typeof choice === 'string') {
      const label = stripLabelQuotes(choice)
      if (label && !(label in map)) map[label] = label
      continue
    }
    const id = String(choice.value ?? choice.id ?? '').trim()
    const label = stripLabelQuotes(choice.text || choice.label)
    if (id && !(id in map) && label) map[id] = label
  }
  for (const opt of r.option_metadata ?? []) {
    if (opt.is_none) continue
    const id = String(opt.id ?? '').trim()
    const label = stripLabelQuotes(opt.label)
    if (id && !(id in map) && label) map[id] = label
  }
  return Object.keys(map).length > 0 ? map : undefined
}

function firstMappedVersionId(raw: unknown, map: Record<string, string>): string | undefined {
  if (raw == null || typeof raw === 'object') {
    if (!Array.isArray(raw)) return undefined
    for (const value of raw) {
      const id = String(value ?? '').trim()
      if (id && map[id]) return id
    }
    return undefined
  }
  const id = String(raw).trim()
  return id && map[id] ? id : undefined
}

function uniqueMentionedVersionLabel(text: string, map: Record<string, string>): string | undefined {
  const hay = text.toLowerCase()
  if (!hay) return undefined
  const labels = Array.from(
    new Set(Object.values(map).map((label) => stripLabelQuotes(label)).filter((label) => label.length >= 2)),
  ).sort((a, b) => b.length - a.length)
  const hits = labels.filter((label) => hay.includes(label.toLowerCase()))
  const unique = hits.filter(
    (label) => !hits.some((other) => other !== label && other.toLowerCase().includes(label.toLowerCase())),
  )
  return unique.length === 1 ? unique[0] : undefined
}

/** Which version a quote is about: item_id (monadic), else the first ranked/picked
 *  option that is a version, else a unique version-name mention in the verbatim.
 *  Cache scope: one-shot, not cached — pure lookup. */
export function resolveQuoteVersionLabel(args: {
  versionLabelById?: Record<string, string>
  itemId?: string | null
  surveyData?: Record<string, unknown>
  quoteText?: string
}): string | undefined {
  const map = args.versionLabelById
  if (!map) return undefined
  const fromItem = map[String(args.itemId ?? '').trim()]
  if (fromItem) return fromItem
  const data = args.surveyData ?? {}
  for (const raw of Object.values(data)) {
    const id = firstMappedVersionId(raw, map)
    if (id) return map[id]
  }
  return uniqueMentionedVersionLabel(args.quoteText ?? '', map)
}
